import * as React from 'react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { Link } from '@fluentui/react/lib/Link';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import styled from 'styled-components';

/** PBT review support alias — the "contact support" link opens a mail to this address. */
const SUPPORT_EMAIL = 'HRCPBTReview@microsoft.com';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('PBT Access Request')}`;

/** Inline mailto link to PBT review support, for embedding directly in a restricted-screen message. */
export function ContactSupportLink({ children }: { children: React.ReactNode }): React.ReactElement {
  return <Link href={SUPPORT_MAILTO}>{children}</Link>;
}

// Sits directly under the app header (no nav), centered on a white background and responsive.
const CenteredStack = styled(Stack)`
  min-height: calc(100vh - 48px);
  padding: 24px 16px;
  text-align: center;
  background: #ffffff;
`;

// Responsive illustration: scales down on small screens, capped on large ones.
const IllustrationWrap = styled.div`
  width: 100%;
  max-width: 180px;
  margin-bottom: 8px;

  svg {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const BodyText = styled(Text)`
  max-width: 480px;
`;

/** Original inline SVG — a friendly padlock, conveying "no access" for both the geo and role blocks. */
function LockIllustration(): React.ReactElement {
  return (
    <svg viewBox="0 0 200 200" role="img" aria-hidden="true" focusable="false">
      <circle cx="100" cy="104" r="92" fill="#f0f6ff" />
      <path
        d="M70 96 V74 a30 30 0 0 1 60 0 V96"
        fill="none"
        stroke="#5b6b7b"
        strokeWidth="11"
        strokeLinecap="round"
      />
      <rect x="56" y="96" width="88" height="70" rx="14" fill="#0f6cbd" />
      <circle cx="100" cy="124" r="10" fill="#ffffff" />
      <rect x="95" y="124" width="10" height="22" rx="5" fill="#ffffff" />
    </svg>
  );
}

interface IAccessRestrictedProps {
  /** Optional override for the heading. Defaults to "Access Restricted". */
  title?: string;
  /** Optional body message. May include inline nodes (e.g. a ContactSupportLink). */
  message?: React.ReactNode;
}

/**
 * Full-screen block shown when the signed-in user is blocked app-wide by the backend (403 on every
 * API call) — either geo-restricted by location, or not provisioned in an authorized PBT security
 * group. Rendered under the app header (no nav, no notifications). The caller supplies the message,
 * which embeds a ContactSupportLink (mailto to PBT review support) inline where appropriate.
 */
export function AccessRestricted({ title, message }: IAccessRestrictedProps): React.ReactElement {
  return (
    <CenteredStack
      verticalAlign="center"
      horizontalAlign="center"
      tokens={{ childrenGap: 10 }}
      role="alert"
      aria-live="assertive"
    >
      <IllustrationWrap>
        <LockIllustration />
      </IllustrationWrap>
      <Text variant="xLarge" as="h1">
        {title ?? 'Access Restricted'}
      </Text>
      {message ? <BodyText variant="medium">{message}</BodyText> : null}
      <PrimaryButton
        text="Reload Page"
        onClick={() => window.location.reload()}
        styles={{ root: { marginTop: 12 } }}
      />
    </CenteredStack>
  );
}
